/**
 *  Promo Config
 *  this config defines the promos and under what conditions they show
 *  items will be evaluated in order so should be from most to least specific
 */

export const isTrivia = buzz =>
  (buzz?.laser_tags?.bf_content_description?.frames_and_formats || []).some(
    tag => tag.tag_name === 'trivia_quiz'
  );

// Returns true when the current device is iOS (SSR-safe)
export const isIosDevice = () =>
  typeof window !== 'undefined' && /iP(hone|ad|od)/i.test(window.navigator.userAgent);

const getAbDebugVariant = (params, experimentName) => {
  if (!params || !params.has('abdebug')) {
    return null;
  }

  return params.get(`abeagle_${experimentName}`);
};

const getExperimentVariant = ({ experiments, params, experimentName }) => {
  const debugVariant = getAbDebugVariant(params, experimentName);
  if (debugVariant !== null) {
    return debugVariant;
  }

  return experiments?.eligible?.[experimentName]?.value;
};

const ruleMatchesVariant = (rule, variant) => {
  // allowedVariants is required so a rule can never accidentally match
  // unintended variants (e.g. 'control'). A rule without it never matches.
  if (!rule?.allowedVariants?.length) {
    return false;
  }

  return rule.allowedVariants.includes(variant);
};

const ruleMatchesBuzz = (rule, buzz) => {
  if (typeof rule?.matchOverride !== 'function') {
    return true;
  }

  return rule.matchOverride(buzz);
};

// Returns true when an experiment is running and the user is not in control.
const isExperimentActive = ({ experiments, params, experimentName }) => {
  const variant = getExperimentVariant({ experiments, params, experimentName });
  return variant != null && variant !== 'control';
};

// Resolves AB-based content overrides for a selected promo.
// Rules are evaluated in order, and the first match wins.
export const resolvePromoVariantContent = ({ promo, buzz, experiments, params }) => {
  const baseContent = promo?.content;
  const rules = promo?.abTestRules;

  if (!baseContent || !Array.isArray(rules) || rules.length === 0) {
    return baseContent;
  }

  const matchedRule = rules.find(rule => {
    if (!rule?.experimentName) {
      return false;
    }

    const variant = getExperimentVariant({
      experiments,
      params,
      experimentName: rule.experimentName,
    });

    return ruleMatchesVariant(rule, variant) && ruleMatchesBuzz(rule, buzz);
  });

  if (!matchedRule?.contentOverride) {
    return baseContent;
  }

  return {
    ...baseContent,
    ...matchedRule.contentOverride,
  };
};

// Links to app stores. campaignId will be appended to these
export const StoreLinks = {
  buzzfeed: {
    'ios': 'https://apps.apple.com/app/apple-store/id352969997?pt=329394&mt=8&ct=',
    'android': 'https://play.google.com/store/apps/details?id=com.buzzfeed.android&utm_source=buzzfeed&utm_campaign=',
    'desktop': 'https://www.buzzfeed.com/app?ct=',
  },
  tasty : {
    'ios': 'https://apps.apple.com/app/apple-store/id1217456898?pt=329394&mt=8&ct=',
    'android': 'https://play.google.com/store/apps/details?id=com.buzzfeed.tasty&utm_source=buzzfeed&utm_medium=web&utm_campaign=',
    'desktop': 'https://www.tasty.co/download?ct=',
  },
  quizParty: {
    'ios': 'https://apps.apple.com/app/buzzfeed-quiz-party/id6759345967?pt=329394&mt=8&ct=',
  },
}

export const PromoData = [
  {
    name: 'tasty',
    enabled: true,
    match: buzz => buzz.category === 'Tasty',
    content: {
      promoType: 'button',
      btnText: '7,500+ recipes, 1 free app',
      campaignId: 'bftasty',
      storeLinks: StoreLinks.tasty,
      unit_name: 'tasty',
      subunit_name: 'tasty_mobile_app_promo',
    }
  },
  {
    name: 'facebook',
    enabled: false,
    match: (buzz, referrer) => referrer == 'facebook',
    content: {
      promoType: 'card',
      titleText: 'Life is better in the app!',
      descText: 'What’s trending, funny tweets, quizzes, games, and more.',
      btnText: 'Get the BuzzFeed app',
      closeText: 'Not now',
      campaignId: 'post-fb',
      storeLinks: StoreLinks.buzzfeed,
    }
  },
  {
    name: 'quiz-party',
    enabled: true,
    match: (buzz, _referrer, { experiments, params } = {}) =>
      buzz.isQuiz &&
      isIosDevice() &&
      isExperimentActive({ experiments, params, experimentName: 'BF-17997-quiz-party-complete-copy' }),
    content: {
      promoType: 'button',
      btnText: 'Try the Quiz Party App!',
      campaignId: 'quiz-party-btn',
      storeLinks: StoreLinks.quizParty,
    }
  },
  {
    name: 'quiz',
    enabled: true,
    match: buzz => buzz.isQuiz,
    content: {
      promoType: 'button',
      btnText: 'More Quizzes in the App!',
      campaignId: 'quiz',
      storeLinks: StoreLinks.buzzfeed,
    }
  },
  {
    name: 'default',
    enabled: false,
    match: buzz => !buzz.isQuiz,
    content: {
      promoType: 'button',
      btnText: 'Get the App!',
      campaignId: 'post',
      storeLinks: StoreLinks.buzzfeed,
    }
  },
  {
    // special promo activated on quiz-party trivia posts after completion (iOS only)
    name: 'quiz-party-trivia-complete',
    enabled: true,
    match: (buzz, _referrer, { experiments, params } = {}) =>
      buzz.isQuiz &&
      isTrivia(buzz) &&
      isIosDevice() &&
      isExperimentActive({ experiments, params, experimentName: 'BF-17997-quiz-party-complete-copy' }),
    content: {
      promoType: 'quiz-party-card',
      titleText: 'Are you the smart one in the friend group?',
      subText: 'Challenge your friends in the new Quiz Party app!',
      btnText: 'Download Now!',
      closeText: 'Not now',
      campaignId: 'quiz-cmplt-triviachallenge',
      storeLinks: StoreLinks.quizParty,
    },
    abTestRules: [
      {
        experimentName: 'BF-17997-quiz-party-complete-copy',
        allowedVariants: ['v1'],
        contentOverride: {
          titleText: 'You wanted a real leaderboard. So we built one.',
          subText: 'Challenge your friends in the new Quiz Party app.',
          campaignId: 'quiz-cmplt-leaderboard',
        },
      },
      {
        experimentName: 'BF-17997-quiz-party-complete-copy',
        allowedVariants: ['v2'],
        contentOverride: {
          titleText: "Someone in your friend group is smarter than you. Maybe.",
          subText: 'Settle it in the new Quiz Party app.',
          campaignId: 'quiz-cmplt-smarter',
        },
      },
      {
        experimentName: 'BF-17997-quiz-party-complete-copy',
        allowedVariants: ['v3'],
        contentOverride: {
          titleText: "Trivia night, but your couch.",
          subText: 'Get your friends in the new Quiz Party app.',
          campaignId: 'quiz-cmplt-couch',
        },
      },
    ],
  },
  {
    // special promo activated on quiz-party posts after completion (iOS only)
    name: 'quiz-party-complete',
    enabled: true,
    match: (buzz, _referrer, { experiments, params } = {}) =>
      buzz.isQuiz &&
      isIosDevice() &&
      isExperimentActive({ experiments, params, experimentName: 'BF-17997-quiz-party-complete-copy' }),
    content: {
      promoType: 'quiz-party-card',
      titleText: 'We made a new quiz app, just for you!',
      subText: 'Quiz Party lets you take quizzes together, on your own time.',
      btnText: 'Download Now!',
      closeText: 'Not now',
      campaignId: 'quiz-cmplt-newapp',
      storeLinks: StoreLinks.quizParty,
    },
    abTestRules: [
      {
        experimentName: 'BF-17997-quiz-party-complete-copy',
        allowedVariants: ['v1'],
        contentOverride: {
          titleText: 'You asked, we built it!',
          subText: 'Take quizzes together in the new Quiz Party app.',
          campaignId: 'quiz-cmplt-youasked',
        },
      },
      {
        experimentName: 'BF-17997-quiz-party-complete-copy',
        allowedVariants: ['v2'],
        contentOverride: {
          titleText: "Don't just share your result. Compare it.",
          subText: 'Find out in the new Quiz Party app.',
          campaignId: 'quiz-cmplt-compare',
        },
      },
      {
        experimentName: 'BF-17997-quiz-party-complete-copy',
        allowedVariants: ['v3'],
        contentOverride: {
          titleText: 'Your best friend needs to take this.',
          subText: 'Get the new Quiz Party app to take it together.',
          campaignId: 'quiz-cmplt-friends',
        },
      },
    ],
  },
  {
    // special promo activated on quizzes after completion
    name: 'quiz-complete',
    enabled: true,
    match: buzz => buzz.isQuiz,
    content: {
      promoType: 'card',
      titleText: 'Quizzes are better in the app!',
      descText: 'What’s trending, funny tweets, quizzes, games, and more.',
      btnText: 'Get the BuzzFeed app',
      closeText: 'Not now',
      campaignId: 'quiz-complete',
      storeLinks: StoreLinks.buzzfeed,
    }
  },
];
